/**
 * ProviderSchedulePage — `/dashboard/provider/schedule`.
 *
 * Phase 2 extraction: the booking columns (Incoming / In progress /
 * Completed) and the calendar view toggle, plus all the related
 * action handlers (start / complete / cancel) and modals (cancel
 * confirm, day drawer, booking detail). The earnings tile, credit
 * card, Stripe card, business-profile editor, and Membership panel
 * each moved to their own sub-route.
 *
 * Reuses PooCalendar + booking-bits + the same testids as the
 * legacy ProviderDashboard so pytest + e2e references survive.
 *
 * Backlog A — calendar is now sourced from the role-neutral
 * `<PooCalendar>` component (consumed here at provider scope via
 * `testidPrefix="provider-calendar"`). M11 adds Day view as a first-
 * class mode; M12 forces Day view on mobile (<768px) and hides the
 * mode toggle so phone users get the only viable layout.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TimezoneBanner from "@/components/TimezoneBanner";
import ImpersonationConfirmGate, {
  useImpersonationGate,
} from "@/components/ImpersonationConfirmGate";
import {
  BookingStatusPill,
  PriceBreakdown,
  RecurringTag,
} from "@/components/booking-bits";
import PooCalendar from "@/components/calendar/PooCalendar";
import { defaultProviderResolver } from "@/components/calendar/useEventStyleResolver";
import {
  Briefcase,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  LayoutList,
  Loader2,
  MapPin,
  PawPrint,
  Play,
  ShieldAlert,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

function Row({ label, amount, bold }) {
  return (
    <div
      className={cn(
        "flex justify-between",
        bold && "border-t border-border/60 pt-1 font-semibold text-pp-text"
      )}
    >
      <span className={!bold ? "text-muted-foreground" : ""}>{label}</span>
      <span>
        {amount < 0
          ? `−$${Math.abs(amount).toFixed(2)}`
          : `$${(amount || 0).toFixed(2)}`}
      </span>
    </div>
  );
}

function BookingCard({ b, onStart, onComplete, onCancel, busy }) {
  const [open, setOpen] = useState(false);
  const isAccepted = b.status === "accepted";
  const isInProgress = b.status === "in_progress";
  const isCompleted = b.status === "completed";
  const ps = b.pricing_snapshot || {};
  return (
    <div
      className="rounded-2xl border border-border bg-pp-surface p-4"
      data-testid={`provider-booking-${b.id}`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <BookingStatusPill status={b.status} />
        <RecurringTag scheduleType={b.schedule_type} />
        {b.unsecured_count > 0 && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800"
            data-testid={`provider-booking-${b.id}-unsecured`}
          >
            <ShieldAlert className="h-3 w-3" />
            {b.unsecured_count} unsecured
          </span>
        )}
      </div>
      <p className="text-base font-semibold text-pp-text">
        {b.customer_name || "Customer"}
      </p>
      <p className="text-sm font-semibold text-pp-text">
        {b.service_date} · {b.preferred_window?.start}–{b.preferred_window?.end}
      </p>
      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
        <MapPin className="h-3 w-3" />
        {b.service_address?.line1}
        {b.service_address?.city ? `, ${b.service_address.city}` : ""}
      </p>
      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
        <PawPrint className="h-3 w-3" />
        {b.dog_count} dog{b.dog_count !== 1 ? "s" : ""}
      </p>
      <div className="mt-3 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Total
          </p>
          <p className="font-heading text-xl font-bold text-pp-text">
            ${(ps.total || 0).toFixed(2)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Your payout
          </p>
          <p className="font-heading text-xl font-bold text-brand-orange">
            ${(ps.provider_payout || 0).toFixed(2)}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="mt-3 flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline"
        data-testid={`provider-booking-${b.id}-toggle-breakdown`}
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {open ? "Hide" : "Show"} price breakdown
      </button>
      {open && (
        <div
          className="mt-2 space-y-1 rounded-xl bg-muted p-3 text-xs"
          data-testid={`provider-booking-${b.id}-breakdown`}
        >
          <Row label="Subtotal" amount={ps.subtotal} bold />
          {ps.recurring_discount_amount > 0 && (
            <Row
              label={`Recurring discount (${ps.recurring_discount_pct}%)`}
              amount={-ps.recurring_discount_amount}
            />
          )}
          <Row label="Total" amount={ps.total} bold />
          <Row
            label={`Platform fee (${ps.commission_pct}%)`}
            amount={-(ps.platform_fee || 0)}
          />
          <Row label="Your payout" amount={ps.provider_payout} bold />
          {ps.processing_fee_enabled === true && (
            <p
              className="mt-2 text-[11px] leading-snug text-muted-foreground"
              data-testid={`provider-booking-${b.id}-fee-footnote`}
            >
              Customer total includes payment processing. Your payout is
              calculated on your listed price — unchanged.
            </p>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {isAccepted && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => onStart(b)}
            className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
            data-testid={`provider-booking-${b.id}-start`}
          >
            <Play className="mr-1 h-3 w-3" /> Start
          </Button>
        )}
        {isInProgress && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => onComplete(b)}
            className="rounded-full bg-green-600 text-white hover:bg-green-700"
            data-testid={`provider-booking-${b.id}-complete`}
          >
            <CheckCircle2 className="mr-1 h-3 w-3" /> Complete
          </Button>
        )}
        {!isCompleted && b.status !== "cancelled" && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => onCancel(b)}
            className="rounded-full text-red-600 hover:bg-red-50 hover:text-red-700"
            data-testid={`provider-booking-${b.id}-cancel`}
          >
            <X className="mr-1 h-3 w-3" /> Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

function BookingSection({
  title,
  Icon,
  items,
  emptyText,
  testId,
  onStart,
  onComplete,
  onCancel,
  busyId,
}) {
  return (
    <Card
      className="rounded-3xl border-border bg-pp-surface p-6 shadow-none"
      data-testid={testId}
    >
      <div className="mb-3 flex items-center gap-2 text-pp-text">
        <Icon className="h-5 w-5" />
        <h3 className="font-heading text-lg font-semibold">{title}</h3>
        <span className="ml-auto text-xs font-bold text-muted-foreground">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl bg-secondary p-6 text-center">
          <p className="text-sm text-muted-foreground">{emptyText}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((b) => (
            <BookingCard
              key={b.id}
              b={b}
              onStart={onStart}
              onComplete={onComplete}
              onCancel={onCancel}
              busy={busyId === b.id}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function formatDrawerDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function ProviderSchedulePage() {
  const { user } = useAuth();
  const { requestAction, gateProps } = useImpersonationGate();
  const [bookings, setBookings] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  // Track B — lightweight provider profile-me fetch to drive the
  // legacy-cohort timezone banner. Kept local (no context provider)
  // to match the "no new context provider just for this" rule.
  const [providerTimezone, setProviderTimezone] = useState(undefined);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/profiles/provider/me");
        if (alive) setProviderTimezone(data?.timezone || null);
      } catch (_) {
        // Silent — banner just stays hidden; not worth toasting.
        if (alive) setProviderTimezone(null);
      }
    })();
    return () => { alive = false; };
  }, []);
  const [view, setView] = useState(
    () => (typeof window !== "undefined" && window.localStorage.getItem("pp_provider_view")) || "list"
  );
  const [calendarMode, setCalendarMode] = useState(
    () => (typeof window !== "undefined" && window.localStorage.getItem("pp_provider_calendar_mode")) || "month"
  );
  const [dayDrawer, setDayDrawer] = useState(null);
  const [detailBooking, setDetailBooking] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("pp_provider_view", view);
  }, [view]);

  // M12 — viewport detection lives at the page level (not inside PooCalendar)
  // so the calendar stays a pure render. Pattern mirrors `Map.jsx`'s matchMedia
  // usage. Below `md:` (768px) the user gets Day view forced + a hidden mode
  // toggle. Above `md:` the user picks Month / Week / Day from the toggle.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  // Skip the localStorage write on mobile so a phone session doesn't
  // overwrite the user's desktop preference.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isMobile) return;
    window.localStorage.setItem("pp_provider_calendar_mode", calendarMode);
  }, [calendarMode, isMobile]);
  // Effective mode passed to <PooCalendar>: forced to "day" on mobile.
  const effectiveCalendarMode = isMobile ? "day" : calendarMode;

  const loadBookings = useCallback(async () => {
    try {
      // `view=provider` disambiguates perspective for multi-cap (tri-role)
      // users so the provider shell always gets the provider-side rows.
      // Idempotent for pure-provider accounts. See
      // /app/memory/bookings_mine_tri_role_plan.md.
      const { data } = await api.get("/bookings/mine", { params: { view: "provider" } });
      setBookings(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const incoming = useMemo(
    () =>
      bookings
        .filter((b) => b.status === "accepted")
        .sort((a, b) => a.service_date.localeCompare(b.service_date)),
    [bookings]
  );
  const inProgress = useMemo(
    () =>
      bookings
        .filter((b) => b.status === "in_progress")
        .sort((a, b) => a.service_date.localeCompare(b.service_date)),
    [bookings]
  );
  const completed = useMemo(
    () =>
      bookings
        .filter((b) => b.status === "completed")
        .sort((a, b) => b.service_date.localeCompare(a.service_date)),
    [bookings]
  );

  const handleStart = (b) => {
    requestAction("mark booking started", async () => {
      setBusyId(b.id);
      try {
        const { data } = await api.post(`/bookings/${b.id}/start`);
        setBookings((bs) => bs.map((x) => (x.id === b.id ? { ...x, ...data } : x)));
        toast.success("Job started");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setBusyId(null);
      }
    });
  };

  const handleComplete = (b) => {
    requestAction("mark booking completed", async () => {
      setBusyId(b.id);
      try {
        const { data } = await api.post(`/bookings/${b.id}/complete`);
        const finished = data?.booking || data;
        setBookings((bs) =>
          bs.map((x) => (x.id === b.id ? { ...x, ...finished } : x))
        );
        if (data?.next_occurrence) {
          toast.success("Job complete · next occurrence created");
        } else {
          toast.success("Job complete");
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setBusyId(null);
      }
    });
  };

  const openCancel = (b) => {
    setCancelTarget(b);
    setCancelReason("");
  };
  const confirmCancel = async () => {
    if (!cancelTarget) return;
    const id = cancelTarget.id;
    setBusyId(id);
    try {
      const { data } = await api.post(`/bookings/${id}/cancel`, {
        reason: cancelReason,
      });
      setBookings((bs) => bs.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Booking cancelled");
      setCancelTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="provider-schedule-page">
      {/* Track B — legacy-cohort timezone banner. Dismissible; hidden
          when the provider's profile.timezone is set. Consumes the
          local `providerTimezone` state (fetched above) to avoid a
          duplicate network call. */}
      <TimezoneBanner
        profileTimezone={providerTimezone}
        settingsHref="/dashboard/provider/profile"
      />
      <header className="mb-6" data-testid="provider-dashboard">
        <span className="text-sm font-bold uppercase tracking-wider text-brand-orange">
          Schedule
        </span>
        <h2 className="font-heading text-3xl font-bold text-pp-text">
          Your jobs
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage incoming, in-progress, and completed Plucks.
        </p>
      </header>

      <div
        className="mb-4 flex flex-wrap items-center justify-between gap-3"
        data-testid="provider-view-toggle"
      >
        <div className="inline-flex rounded-full border border-border bg-pp-surface p-1">
          <button
            type="button"
            onClick={() => setView("list")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
              view === "list" ? "bg-brand-orange text-white" : "text-pp-text hover:text-brand-orange"
            )}
            data-testid="provider-view-list"
          >
            <LayoutList className="h-4 w-4" /> List
          </button>
          <button
            type="button"
            onClick={() => setView("calendar")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
              view === "calendar" ? "bg-brand-orange text-white" : "text-pp-text hover:text-brand-orange"
            )}
            data-testid="provider-view-calendar"
          >
            <CalendarDays className="h-4 w-4" /> Calendar
          </button>
        </div>
        {view === "calendar" && !isMobile && (
          <div
            className="inline-flex rounded-full border border-border bg-pp-surface p-1"
            data-testid="provider-calendar-mode-toggle"
          >
            {["month", "week", "day"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setCalendarMode(m)}
                className={cn(
                  "rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-wider transition-colors",
                  calendarMode === m ? "bg-brand-slate text-white" : "text-pp-text hover:text-brand-orange"
                )}
                data-testid={`provider-calendar-mode-${m}`}
              >
                {m}
              </button>
            ))}
          </div>
        )}
      </div>

      {view === "list" ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <BookingSection
            title="Incoming"
            Icon={Clock}
            items={incoming}
            emptyText="No incoming jobs."
            testId="provider-incoming-card"
            onStart={handleStart}
            onComplete={handleComplete}
            onCancel={openCancel}
            busyId={busyId}
          />
          <BookingSection
            title="In progress"
            Icon={Briefcase}
            items={inProgress}
            emptyText="No jobs in progress."
            testId="provider-inprogress-card"
            onStart={handleStart}
            onComplete={handleComplete}
            onCancel={openCancel}
            busyId={busyId}
          />
          <BookingSection
            title="Completed"
            Icon={CheckCircle2}
            items={completed}
            emptyText="No completed jobs yet."
            testId="provider-completed-card"
            onStart={handleStart}
            onComplete={handleComplete}
            onCancel={openCancel}
            busyId={busyId}
          />
        </div>
      ) : (
        <Card
          className="rounded-3xl border-border bg-pp-surface p-4 shadow-none"
          data-testid="provider-calendar-card"
        >
          <PooCalendar
            events={bookings}
            mode={effectiveCalendarMode}
            views={["month", "week", "day"]}
            eventStyleResolver={defaultProviderResolver}
            onSelectEvent={(b) => setDetailBooking(b)}
            onSelectSlot={(iso) => setDayDrawer(iso)}
            ariaLabel="Provider schedule calendar"
            testidPrefix="provider-calendar"
          />
        </Card>
      )}

      <Dialog
        open={!!cancelTarget}
        onOpenChange={(o) => !o && setCancelTarget(null)}
      >
        <DialogContent className="rounded-3xl" data-testid="provider-cancel-modal">
          <DialogHeader>
            <DialogTitle>Cancel this booking?</DialogTitle>
          </DialogHeader>
          {user?.is_impersonating && (
            <div
              className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
              data-testid="provider-cancel-impersonation-banner"
            >
              <div className="flex items-start gap-2">
                <ShieldAlert
                  className="mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden="true"
                />
                <span>
                  You&rsquo;re acting as{" "}
                  <strong className="font-semibold">{user.email}</strong>.
                  Confirm this <strong className="font-semibold">cancel
                  this booking</strong> on their account?
                </span>
              </div>
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            This will notify the customer. Add a short reason so they know why.
          </p>
          <Textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="e.g. Weather, vehicle issue…"
            className="mt-3 rounded-2xl"
            rows={3}
            data-testid="provider-cancel-reason"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setCancelTarget(null)}
              className="rounded-full"
              data-testid="provider-cancel-modal-back"
            >
              Keep booking
            </Button>
            <Button
              onClick={confirmCancel}
              disabled={busyId === cancelTarget?.id}
              className="rounded-full bg-red-600 text-white hover:bg-red-700"
              data-testid="provider-cancel-modal-confirm"
            >
              {busyId === cancelTarget?.id ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Cancel booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dayDrawer} onOpenChange={(o) => !o && setDayDrawer(null)}>
        <DialogContent className="rounded-3xl" data-testid="provider-day-drawer">
          <DialogHeader>
            <DialogTitle>
              {dayDrawer ? formatDrawerDate(dayDrawer) : ""}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            if (!dayDrawer) return null;
            const dayBookings = bookings
              .filter((b) => b.service_date === dayDrawer && b.status !== "cancelled")
              .sort((a, b) =>
                (a.preferred_window?.start || "").localeCompare(b.preferred_window?.start || "")
              );
            if (!dayBookings.length) {
              return (
                <p className="rounded-2xl bg-secondary p-6 text-center text-sm text-muted-foreground">
                  No jobs on this day.
                </p>
              );
            }
            return (
              <div className="space-y-3">
                {dayBookings.map((b) => (
                  <div
                    key={b.id}
                    className="rounded-2xl border border-border p-3"
                    data-testid={`day-drawer-booking-${b.id}`}
                  >
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <BookingStatusPill status={b.status} />
                      <RecurringTag scheduleType={b.schedule_type} />
                    </div>
                    <p className="text-sm font-semibold text-pp-text">
                      {b.preferred_window?.start}–{b.preferred_window?.end} · {b.customer_name || "Customer"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {b.dog_count} dog{b.dog_count !== 1 ? "s" : ""} · ${(b.pricing_snapshot?.total || 0).toFixed(2)}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setDayDrawer(null);
                        setDetailBooking(b);
                      }}
                      className="mt-2 rounded-full text-brand-orange hover:bg-brand-orange/10"
                      data-testid={`day-drawer-view-${b.id}`}
                    >
                      View details
                    </Button>
                  </div>
                ))}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailBooking} onOpenChange={(o) => !o && setDetailBooking(null)}>
        <DialogContent className="rounded-3xl" data-testid="provider-booking-detail-modal">
          <DialogHeader>
            <DialogTitle>
              {detailBooking?.customer_name || "Booking"} · {detailBooking?.service_date}
            </DialogTitle>
          </DialogHeader>
          {detailBooking && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <BookingStatusPill status={detailBooking.status} />
                <RecurringTag scheduleType={detailBooking.schedule_type} />
                {detailBooking.unsecured_count > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">
                    <ShieldAlert className="h-3 w-3" />
                    {detailBooking.unsecured_count} unsecured
                  </span>
                )}
              </div>
              <p className="text-sm font-semibold text-pp-text">
                {detailBooking.preferred_window?.start}–{detailBooking.preferred_window?.end}
              </p>
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3" />
                {detailBooking.service_address?.line1}
                {detailBooking.service_address?.city ? `, ${detailBooking.service_address.city}` : ""}
              </p>
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <PawPrint className="h-3 w-3" />
                {detailBooking.dog_count} dog{detailBooking.dog_count !== 1 ? "s" : ""}
              </p>
              <div className="rounded-xl bg-muted p-3">
                <PriceBreakdown
                  snapshot={detailBooking.pricing_snapshot}
                  dogCount={detailBooking.dog_count}
                  unsecuredCount={detailBooking.unsecured_count}
                  compact
                />
              </div>
              <div className="rounded-xl bg-brand-orange/10 p-3 text-right">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Your payout</p>
                <p className="font-heading text-2xl font-bold text-brand-orange">
                  ${(detailBooking.pricing_snapshot?.provider_payout || 0).toFixed(2)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {detailBooking.status === "accepted" && (
                  <Button
                    size="sm"
                    disabled={busyId === detailBooking.id}
                    onClick={() => {
                      handleStart(detailBooking);
                      setDetailBooking(null);
                    }}
                    className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
                    data-testid="detail-start-button"
                  >
                    <Play className="mr-1 h-3 w-3" /> Start
                  </Button>
                )}
                {detailBooking.status === "in_progress" && (
                  <Button
                    size="sm"
                    disabled={busyId === detailBooking.id}
                    onClick={() => {
                      handleComplete(detailBooking);
                      setDetailBooking(null);
                    }}
                    className="rounded-full bg-green-600 text-white hover:bg-green-700"
                    data-testid="detail-complete-button"
                  >
                    <CheckCircle2 className="mr-1 h-3 w-3" /> Complete
                  </Button>
                )}
                {detailBooking.status !== "completed" && detailBooking.status !== "cancelled" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busyId === detailBooking.id}
                    onClick={() => {
                      openCancel(detailBooking);
                      setDetailBooking(null);
                    }}
                    className="rounded-full text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid="detail-cancel-button"
                  >
                    <X className="mr-1 h-3 w-3" /> Cancel
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ImpersonationConfirmGate {...gateProps} />
    </div>
  );
}
